import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CemeteryDto, PartialMessageUpdateDto } from '../../../services/Maintenance/maintenanceService.dto';
import { DataTableRef } from '../../../components/table/dataTableRef';
import DataTable, { Column, TableFilterStateDto } from '../../../components/table/DataTable';
import { dateUtils } from '../../../utils/dateUtils';
import {
  convertFiltersToCemeteryFilterDto,
  getRoutingKeyAsString,
  toPartialMessageUpdateDto,
} from '../utils/maintenanceConverter';
import { maintenanceService } from '../../../services/Maintenance/maintenanceService';
import { useError } from '../../../providers/useError';
import { usePopup } from '../../../providers/PopupProvider';
import CemeteryActions from './CemeteryActions';
import JSONCell from './JSONCell';
import JSONPopover from './JSONPopover';

interface Props {
  messagesInDatabaseCount: (count: number) => void;
  filtersMap?: Map<string, any>;
}

const CemeteryTable = (props: Props) => {
  const { t } = useTranslation('maintenance');
  const [messageDto, setMessageDto] = useState<CemeteryDto>();
  const dataTableRef = useRef<DataTableRef>(null);
  const { handleError } = useError();
  const { showPopover, hidePopover, showConfirmPopover } = usePopup();

  const columns: Column<CemeteryDto>[] = [
    {
      id: 'id',
      title: t('cemetery.columns.id'),
      renderCell: data => data.id,
    },
    {
      id: 'headers',
      title: t('cemetery.columns.headers'),
      renderCell: data => (
        <JSONCell
          jsonData={data.headers}
          onPress={(element, JSONData) => {
            showPopover(<JSONPopover JSONString={JSONData} onConfirm={requeue} />, element);
          }}
        />
      ),
    },
    {
      id: 'created',
      title: t('cemetery.columns.pulling'),
      minWidth: 100,
      isSortable: true,
      renderCell: data => dateUtils.toLocalDateTimeString(data.created),
    },
    {
      id: 'firstDeathExchange',
      title: t('cemetery.columns.first-death-exchange'),
      renderCell: data => data.firstDeathExchange,
    },
    {
      id: 'payload',
      title: t('cemetery.columns.payload'),
      renderCell: data => (
        <JSONCell
          jsonData={data.payload}
          onPress={(element, JSONData) => {
            showPopover(<JSONPopover JSONString={JSONData} onConfirm={requeue} />, element);
          }}
        />
      ),
    },
    {
      id: 'lastDeathTime',
      title: t('cemetery.columns.last-death-date'),
      minWidth: 100,
      isSortable: true,
      renderCell: data => dateUtils.toLocalDateTimeString(data.lastDeathTime),
    },
    {
      id: 'firstDeathQueue',
      title: t('cemetery.columns.first-death-queue'),
      renderCell: data => data.firstDeathQueue,
    },
    {
      id: 'lastDeathQueue',
      title: t('cemetery.columns.last-death-queue'),
      renderCell: data => data.lastDeathQueue,
    },
    {
      id: 'deleted',
      title: t('cemetery.columns.deleted'),
      renderCell: data => (data.deleted ? 'Yes' : 'No'),
    },
    {
      id: 'requeued',
      title: t('cemetery.columns.requeued'),
      renderCell: data => (data.requeued ? 'Yes' : 'No'),
    },
    {
      id: 'updated',
      title: t('cemetery.columns.update'),
      minWidth: 100,
      isSortable: true,
      renderCell: data => dateUtils.toLocalDateTimeString(data.updated),
    },
    {
      id: 'pulledBy',
      title: t('cemetery.columns.pulled-by'),
      renderCell: data => data.pulledBy,
    },
    {
      id: 'actions',
      title: t('cemetery.columns.actions'),
      renderCell: data => (
        <CemeteryActions
          messageDto={data}
          onDeleteClick={handleDeleteClick}
          onRequeueClick={onRequeue}
          onRetryClick={onRetry}
        />
      ),
    },
  ];

  const handleDeleteClick = (message: CemeteryDto) => {
    showConfirmPopover(t('cemetery.popover.delete-confirm'), deleteMessage);
    setMessageDto(message);
  };

  const onRequeue = (message: CemeteryDto, anchor: SVGSVGElement) => {
    setMessageDto(message);
    showPopover(<JSONPopover onConfirm={requeue} partialMessage={toPartialMessageUpdateDto(message)} />, anchor);
  };

  const deleteMessage = () => {
    if (messageDto)
      maintenanceService
        .deleteMessage(messageDto.id)
        .then(() => dataTableRef.current?.refresh())
        .catch(e => handleError(e));
  };

  const requeue = (editedPartialMessage: PartialMessageUpdateDto) => {
    if (messageDto) {
      const requeueMessageDto: PartialMessageUpdateDto = {
        ...editedPartialMessage,
        payload: JSON.stringify(editedPartialMessage.payload),
      };
      if (messageDto.payload === requeueMessageDto.payload) requeueMessageDto.payload = undefined;
      if (requeueMessageDto.requeuedToExchange === '') requeueMessageDto.requeuedToExchange = undefined;
      maintenanceService
        .requeueMessage(messageDto.id, requeueMessageDto)
        .then(() => dataTableRef.current?.refresh())
        .catch(e => handleError(e));
    }
    hidePopover();
  };

  const onRetry = (message: CemeteryDto) => {
    const partialMessageDto: PartialMessageUpdateDto = {
      requeuedToExchange: message.firstDeathExchange,
      requeuedRoutingKey: getRoutingKeyAsString(message),
    };
    maintenanceService
      .requeueMessage(message.id, partialMessageDto)
      .then(() => dataTableRef.current?.refresh())
      .catch(e => handleError(e));
  };

  const handleOnTableStateChanged = useCallback((tableState: TableFilterStateDto<Map<string, any>>) => {
    const filters = convertFiltersToCemeteryFilterDto(tableState);
    return maintenanceService.fetchCemeteryMessages(filters).then(response => {
      props.messagesInDatabaseCount(response.data.totalElements || 0);
      return response.data;
    });
  }, []);

  return (
    <DataTable
      columns={columns}
      onTableStateChanged={handleOnTableStateChanged}
      filterData={props.filtersMap}
      ref={dataTableRef}
    />
  );
};
export default CemeteryTable;
